import { useEffect, useState } from "react";
import { X, MapPin, Hexagon, ShieldCheck, ExternalLink, Loader2 } from "lucide-react";
import { api, shorten } from "@/lib/api";
import { useWallet } from "@/context/WalletContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export function SectorPanel({ sectorId, onClose, onRegistered }) {
  const { account, settings, registerSector } = useWallet();
  const { t } = useLang();
  const [sector, setSector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!sectorId) return;
    setLoading(true);
    api.get(`/sectors/${sectorId}`)
      .then((r) => setSector(r.data))
      .catch(() => setSector(null))
      .finally(() => setLoading(false));
  }, [sectorId]);

  const handleRegister = async () => {
    setBusy(true);
    const res = await registerSector(sector);
    setBusy(false);
    if (res.ok) {
      toast.success("Sector registered", {
        description: res.simulated
          ? "Ownership recorded. Configure treasury for live on-chain settlement."
          : "On-chain payment confirmed. NFT registered.",
      });
      const r = await api.get(`/sectors/${sectorId}`);
      setSector(r.data);
      onRegistered && onRegistered();
    }
  };

  const owned = sector && sector.status !== "available";
  const mine = owned && account && sector.owner_wallet === account;

  return (
    <div
      data-testid="sector-panel"
      className="fixed top-16 right-0 z-40 h-[calc(100vh-4rem)] w-full sm:w-[420px] glass border-l border-cosmic/15 overflow-y-auto fade-up"
    >
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-overline">{t.sector.registry}</div>
            <h3 className="font-display text-2xl mt-2 tracking-wide">
              {loading ? "—" : sector?.sector_id}
            </h3>
          </div>
          <button data-testid="sector-panel-close" onClick={onClose} className="p-2 text-cosmic hover:text-white">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-cosmic/60">
            <Loader2 className="animate-spin" />
          </div>
        ) : sector ? (
          <div className="mt-8 space-y-6">
            <div>
              <div className="text-overline mb-1">{t.sector.designation}</div>
              <div className="text-lg text-white font-grotesk">{sector.name}</div>
            </div>

            <div className="grid grid-cols-2 gap-px bg-cosmic/10 border border-cosmic/10">
              <Field icon={<MapPin size={13} />} label={t.sector.latitude} value={sector.lat_label} />
              <Field icon={<MapPin size={13} />} label={t.sector.longitude} value={sector.lng_label} />
              <Field icon={<Hexagon size={13} />} label={t.sector.surface} value={`${sector.surface_area} km²`} />
              <Field icon={<Hexagon size={13} />} label={t.sector.token} value={`#${sector.token_id}`} />
            </div>

            <div className="border border-cosmic/10 p-5">
              <div className="flex items-center justify-between">
                <span className="text-overline">{t.sector.status}</span>
                <span
                  data-testid="sector-status"
                  className={`font-mono text-sm uppercase tracking-widest ${
                    sector.status === "available" ? "text-portal" : sector.status === "for_sale" ? "text-mars" : "text-cosmic"
                  }`}
                >
                  {sector.status === "available" ? t.sector.available : sector.status === "for_sale" ? t.sector.listed : t.sector.registered}
                </span>
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className="text-overline">{t.sector.cost}</span>
                <span className="font-display text-xl text-white">{sector.price} <span className="text-sm text-cosmic">USDC</span></span>
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className="text-overline">{t.sector.custodian}</span>
                <span className="font-mono text-sm text-cosmic">
                  {owned ? (sector.owner_name || shorten(sector.owner_wallet)) : t.sector.unclaimed}
                </span>
              </div>
            </div>

            {owned && (
              <div className="border border-cosmic/10 p-5 space-y-3 text-xs font-mono text-cosmic/70">
                <div className="text-overline mb-2">{t.sector.record}</div>
                <div className="flex justify-between"><span>{t.sector.network}</span><span>{settings?.network || "Polygon"}</span></div>
                <div className="flex justify-between"><span>{t.sector.standard}</span><span>ERC-721</span></div>
                <div className="flex justify-between"><span>{t.sector.tx}</span><span>{shorten(sector.tx_hash)}</span></div>
                <a
                  href={`${settings?.opensea_base_url || "https://opensea.io/assets/matic"}/${settings?.erc721_contract_address || ""}/${sector.token_id}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-portal hover:underline pt-1"
                >
                  {t.sector.opensea} <ExternalLink size={11} />
                </a>
              </div>
            )}

            {!owned && (
              <button
                data-testid="discover-sector-btn"
                onClick={handleRegister}
                disabled={busy}
                className="btn-portal w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                {busy ? t.sector.processing : account ? t.sector.discover : t.sector.connect}
              </button>
            )}
            {mine && (
              <div className="text-center text-sm text-portal font-mono tracking-widest border border-portal/30 py-3">
                {t.sector.you}
              </div>
            )}

            {!settings?.payment_enabled && !owned && (
              <p className="text-[0.7rem] text-cosmic/40 font-mono leading-relaxed">
                {t.sector.note}
              </p>
            )}
          </div>
        ) : (
          <p className="text-cosmic/60 mt-10">{t.sector.unavailable}</p>
        )}
      </div>
    </div>
  );
}

function Field({ icon, label, value }) {
  return (
    <div className="bg-deepspace/60 p-4">
      <div className="flex items-center gap-1.5 text-overline mb-1.5">{icon}{label}</div>
      <div className="font-mono text-sm text-white">{value}</div>
    </div>
  );
}
